import React from 'react';
import { fmtCount } from '../../utils/formatters';
import { formatRange } from '../../utils/dataEnds';
import { CONDITION_DAYS, CONDITION_SHORT, NotYet, conditionGlyph } from '../../utils/weather';
import WeatherGlyph from './WeatherGlyph';
import { NOTE } from './weatherUi';

// A condition with no shown pattern: how close it is, in counts only. Never a
// number for a pattern that isn't shown.

// Only a gate still short of days has a count to fill. The backend sends
// not_checked_yet once the days are there (n_days >= needed) but too few are
// recent enough to test on.
const COUNTED = ['not_enough_days'];

export default function ProgressRow({ gate }: { gate: NotYet }) {
  const short = CONDITION_SHORT[gate.condition] ?? gate.condition;
  const days = CONDITION_DAYS[gate.condition] ?? 'days like this';
  const counted = COUNTED.includes(gate.status) && gate.n_days != null && gate.needed != null;
  let line = gate.text;
  if (counted) {
    line = `${short}: ${fmtCount(gate.n_days as number)} of ${fmtCount(gate.needed as number)} ${days} needed`;
    if (gate.n_ordinary != null && gate.needed_ordinary != null) {
      // Past the bar it reads as met ("20 of 20"), never "184 of 20".
      line += `, ${fmtCount(Math.min(gate.n_ordinary, gate.needed_ordinary))} of ${fmtCount(gate.needed_ordinary)} ordinary days`;
    }
  } else if (gate.status === 'not_checked_yet' && gate.n_days != null) {
    line = `${short}: ${fmtCount(gate.n_days)} ${days}, not enough recent ones to check yet`;
  } else if (gate.status === 'failed_holdout') line = `${short}: no pattern that holds up yet`;
  const share = counted ? Math.min(1, (gate.n_days as number) / Math.max(1, gate.needed as number)) : 0;
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="mt-0.5 text-[#4A2D5E]"><WeatherGlyph kind={conditionGlyph(gate.condition)} /></span>
      <div className="min-w-0 flex-1">
        <p className="font-sans text-sm text-[#2D2A2E]">{line}</p>
        {counted && (
          <div
            role="progressbar" aria-label={`${days} counted`} aria-valuemin={0}
            aria-valuenow={gate.n_days} aria-valuemax={gate.needed}
            className="mt-1.5 h-1.5 w-full max-w-[16rem] overflow-hidden rounded-full bg-[#F0EBF5]"
          >
            <div className="h-full rounded-full bg-[#4A2D5E]" style={{ width: `${share * 100}%` }} />
          </div>
        )}
        {counted && <p className={`${NOTE} mt-1`}>{gate.text}</p>}
        {!counted && gate.status === 'failed_holdout' && gate.holdout && (
          <p className={`${NOTE} mt-1`}>Tested on {formatRange(gate.holdout.window_start, gate.holdout.window_end)}</p>
        )}
      </div>
    </div>
  );
}
